import React from 'react';
import { Home, Tv, Film, TvMinimal, Star, Settings, Calendar, LogOut, Grid3x3, List, LayoutGrid, Search } from 'lucide-react';
import { useAppStore } from '@/store/app-store';
import { xtreamAPI } from '@/services/xtream-api';
import clsx from 'clsx';

export const TopNav: React.FC = () => {
  const {
    currentView,
    setCurrentView,
    logout,
    authInfo,
    theme,
    channelViewMode,
    setChannelViewMode,
    setGlobalSearchOpen,
  } = useAppStore();

  const handleLogout = () => {
    xtreamAPI.logout();
    logout();
  };

  const menuItems = [
    { id: 'home', icon: Home, label: 'Home', view: 'home' as const, description: 'Discover' },
    { id: 'live', icon: Tv, label: 'Live TV', view: 'live' as const, description: 'Watch live channels' },
    { id: 'vod', icon: Film, label: 'Movies', view: 'vod' as const, description: 'Browse movies' },
    { id: 'series', icon: TvMinimal, label: 'Series', view: 'series' as const, description: 'TV shows and series' },
    { id: 'epg', icon: Calendar, label: 'TV Guide', view: 'epg' as const, description: 'Electronic Program Guide' },
    { id: 'favorites', icon: Star, label: 'Favorites', view: 'favorites' as const, description: 'Your favorites' },
    { id: 'settings', icon: Settings, label: 'Settings', view: 'settings' as const, description: 'App settings' },
  ];

  const viewModeIcons = {
    grid: Grid3x3,
    list: List,
    tile: LayoutGrid,
  };

  return (
    <nav
      className={clsx(
        'sticky top-0 z-50 border-b transition-colors',
        theme === 'dark'
          ? 'bg-gradient-to-r from-[#0c1424]/95 via-[#0c101b]/90 to-[#0c1424]/95 backdrop-blur-xl border-dark-700'
          : 'bg-white/95 backdrop-blur-sm border-gray-300'
      )}
    >
      <div className="flex items-center justify-between gap-2 px-3 sm:px-6 py-3">
        {/* Logo Section */}
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <img
              src="/hero-image.jpeg"
              alt="RedHood"
              className="w-10 h-10 rounded-lg object-cover border-2 border-primary-600 shadow-md"
            />
            <div>
              <h1 className={clsx(
                'text-xl font-bold',
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                RedHood
              </h1>
              {authInfo && (
                <p className="text-xs text-gray-400">
                  {authInfo.user_info.username}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Items - Horizontal (scrolls on small screens) */}
        <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto scrollbar-hide min-w-0">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.view;

            return (
              <button
                key={item.id}
                onClick={() => setCurrentView(item.view)}
                title={item.description}
                className={clsx(
                  'flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all font-medium text-sm focus-strong flex-shrink-0',
                  isActive
                    ? 'bg-primary-600/90 text-white shadow-lg shadow-primary-900/40 scale-105'
                    : theme === 'dark'
                    ? 'text-gray-300 hover:bg-white/5 hover:text-white hover:scale-105'
                    : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900 hover:scale-105'
                )}
              >
                <Icon size={18} />
                <span className="hidden lg:inline">{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Right Section - Search, View Mode & Logout */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {/* Global Search */}
          <button
            onClick={() => setGlobalSearchOpen(true)}
            title="Search (press /)"
            className={clsx(
              'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm font-medium focus-strong',
              theme === 'dark'
                ? 'text-gray-300 hover:bg-white/10 hover:text-white'
                : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'
            )}
          >
            <Search size={18} />
            <span className="hidden xl:inline">Search</span>
          </button>

          {/* View Mode Selector - Only show for content views */}
          {(currentView === 'live' || currentView === 'vod' || currentView === 'series') && (
            <div className={clsx(
              'flex items-center gap-1 p-1 rounded-lg shadow-inner',
              theme === 'dark' ? 'bg-white/5' : 'bg-gray-200'
            )}>
              {(['grid', 'list', 'tile'] as const).map((mode) => {
                const ModeIcon = viewModeIcons[mode];
                return (
                  <button
                    key={mode}
                    onClick={() => setChannelViewMode(mode)}
                    title={`${mode.charAt(0).toUpperCase() + mode.slice(1)} view`}
                    className={clsx(
                      'p-2 rounded transition-all focus-strong',
                      channelViewMode === mode
                        ? 'bg-primary-600 text-white shadow-md shadow-primary-900/40'
                        : theme === 'dark'
                        ? 'text-gray-400 hover:text-white hover:bg-white/10'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-300'
                    )}
                  >
                    <ModeIcon size={16} />
                  </button>
                );
              })}
            </div>
          )}

          {/* Account Status Badge */}
          {authInfo && (
            <div className={clsx(
              'hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs',
              theme === 'dark' ? 'bg-white/5 border border-white/5' : 'bg-gray-200'
            )}>
              <span className={authInfo.user_info.status === 'Active' ? 'text-green-500' : 'text-red-500'}>
                {authInfo.user_info.status === 'Active' ? '● Active' : '● Inactive'}
              </span>
            </div>
          )}

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            title="Logout"
            className={clsx(
              'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm font-medium focus-strong',
              theme === 'dark'
                ? 'text-gray-400 hover:bg-white/10 hover:text-white'
                : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'
            )}
          >
            <LogOut size={18} />
            <span className="hidden xl:inline">Logout</span>
          </button>
        </div>
      </div>
    </nav>
  );
};
